// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Shared, framework-agnostic wire types + helpers for the homeserver API. Used by the
// web client (web/) — which is also the native app via Capacitor — so the data contract
// has one source of truth. UI stays platform-specific; the crypto core is shared at the
// Go level (the WASM engine in cmd/wasm).

export interface DeviceIdentity {
  device_id: string;
  fingerprint: string;
  armored_public_key: string;
}

export interface Session {
  user_id: string;
  device_id: string;
  access_token: string;
}

// RegistrationConfig is the public sign-up policy advertised by the homeserver.
export interface RegistrationConfig {
  mode: "public" | "invite" | "none";
  member_invites: boolean; // regular members (not only admins) may mint invites
  webauthn: boolean; // passkey login is available on this server
  oidc_providers: OIDCProviderInfo[]; // configured "Sign in with …" providers
}

// OIDCProviderInfo is one configured "Sign in with …" provider (id + display name).
export interface OIDCProviderInfo {
  id: string;
  name: string;
}

// RegistrationToken is the safe (secret-free) view of a LOCAL registration invite.
export interface RegistrationToken {
  id: string;
  created_by: string;
  uses_allowed: number;
  uses_used: number;
  expires_at?: number; // UnixMilli; absent = never
  created_at: number; // UnixMilli
}

// PasskeyInfo is one registered WebAuthn credential (passkey / security key).
export interface PasskeyInfo {
  id: string; // base64url credential ID
  name: string;
  created_at: number; // UnixMilli
}

// AdminReport is one abuse report in the moderation queue.
export interface AdminReport {
  id: string;
  reporter: string;
  room_id: string;
  event_id?: string;
  reason: string;
  content?: string; // reporter-disclosed plaintext of the reported message (E2E)
  target_sender?: string; // the reported message's author
  discussion_room?: string; // a moderator↔reporter room opened for context
  status: "open" | "resolved" | "dismissed";
  created_at: number; // UnixMilli
  resolved_by?: string;
  resolved_at?: number; // UnixMilli
}

// AdminAccount is one account row in the admin directory.
export interface AdminAccount {
  user_id: string;
  localpart: string;
  deactivated: boolean;
  admin: boolean;
  invited_by?: string;
  created_at: number; // UnixMilli
}

// AdminRoom is one room summary in the admin console.
export interface AdminRoom {
  room_id: string;
  alias?: string;
  name?: string;
  members: number;
}

// A client command sent over the /sync WebSocket. The server derives identity from
// the token; `sender` is never trusted from the client.
export interface ClientCommand {
  action: "send" | "state" | "join" | "knock" | "leave" | "invite" | "kick" | "ban" | "subscribe" | "typing" | "receipt";
  room_id: string;
  type?: string;
  state_key?: string;
  content?: Record<string, unknown>;
  typing?: boolean;
  event_id?: string;
}

export interface RoomEvent {
  event_id: string;
  room_id: string;
  sender: string;
  type: string;
  state_key?: string | null;
  content: Record<string, unknown>;
  origin_server_ts: number;
  redacted_by?: string;
  // Client-only: the decrypted inner content of an m.room.encrypted event, once
  // the crypto engine has unwrapped it for this device.
  decryptedContent?: Record<string, unknown>;
}

export interface RoomMember {
  user_id: string;
  devices: DeviceIdentity[];
  online?: boolean;
  last_seen?: number; // UnixMilli last went offline (0/absent = online or unknown)
}

// DirectoryRoom is one entry in the channel directory / joined-rooms listing.
export interface DirectoryRoom {
  room_id: string;
  alias?: string;
  name?: string;
  topic?: string;
  join_rule: string;
  room_type?: string; // "m.space" for Spaces
  num_joined_members: number;
  unread?: number; // joined_rooms: messages since the read marker (cross-session)
}

// A client-side room the user is participating in.
export interface Room {
  id: string;
  name: string;
  type?: string; // "m.space" for Spaces (a container, not a chat channel)
  events: RoomEvent[];
  members: string[]; // joined user IDs (resolved from the members endpoint)
  callRoom?: boolean; // an EPHEMERAL room spun up only to host an escalated group call —
  // never a chat (hidden from the rail; everyone leaves it when the call ends)
}

// A channel linked under a Space via m.space.child. The name/alias ride the link
// so space members can discover children that aren't in the public directory.
export interface SpaceChild {
  id: string;
  name: string;
  alias: string;
  suggested?: boolean; // recommended to new members
  order?: string; // lexicographic ordering hint (Matrix-style)
}

// friendlyRoomName prefers a human name, then the alias localpart, never the raw
// "!hash:server" room ID (used in the directory / profile / discovery lists).
export function friendlyRoomName(r: { name?: string; alias?: string; room_id?: string }): string {
  if (r.name) return r.name;
  if (r.alias) return r.alias.replace(/^#/, "").split(":")[0];
  return (r.room_id ?? "").replace(/^[!#]/, "").split(":")[0] || "channel";
}

// A sigchain link (device add/revoke/rotate, or an identity "proof").
export interface SigchainLink {
  seqno: number;
  prev_hash: string;
  device_id?: string;
  armored_public_key?: string;
  action: string;
  signed_by: string;
  signature?: string;
  service?: string;
  identity?: string;
  proof_url?: string;
}

export interface Sigchain {
  user_id: string;
  anchor_key: string;
  links: SigchainLink[];
}

// A validated identity proof (the account cryptographically asserts an external
// identity). Surfaced by the WASM validateSigchain result.
export interface IdentityProof {
  service: string;
  identity: string;
  proof_url: string;
  seqno: number;
  signature?: string; // published at proof_url to prove ownership of the external identity
}

export interface SigchainValidation {
  user_id: string;
  validated_links: number;
  trusted_devices: Record<string, unknown>;
  proofs: IdentityProof[];
}

// powerLevels we track per room to render roles (Op/Voice) IRC-style.
export type PowerLevels = Record<string, number>;

// PowerLevelsContent is the full m.room.power_levels content — tracked so the
// client can read-modify-write it for moderation (promote/demote).
export interface PowerLevelsContent {
  users?: PowerLevels;
  users_default?: number;
  events?: Record<string, number>;
  events_default?: number;
  kick?: number;
  ban?: number;
  redact?: number;
  invite?: number;
  state_default?: number;
  slow_mode_seconds?: number;
  user_rate_limits?: Record<string, number>;
}

export const Power = {
  Founder: 100,
  Op: 50,
  Voice: 10,
  Regular: 0,
} as const;

// roleSigil maps a power level to the IRC nick prefix.
export function roleSigil(level: number): string {
  if (level >= Power.Founder) return "~"; // founder / ChanServ
  if (level >= Power.Op) return "@"; // operator
  if (level >= Power.Voice) return "+"; // voice
  return "";
}

// roleName is the human label for a power level (for tooltips / menus).
export function roleName(level: number): string {
  if (level >= Power.Founder) return "Founder";
  if (level >= Power.Op) return "Operator";
  if (level >= Power.Voice) return "Voice";
  return "Member";
}

// A complete default power-levels table with the creator as founder and the
// protective IRC thresholds intact (kick/ban/state changes require Operator).
export function defaultPowerLevels(creator: string): PowerLevelsContent {
  return {
    users: { [creator]: Power.Founder },
    users_default: Power.Regular,
    events: { "m.room.power_levels": Power.Founder },
    events_default: Power.Regular,
    kick: Power.Op,
    ban: Power.Op,
    redact: Power.Op,
    invite: Power.Op,
    state_default: Power.Op,
  };
}

// Key transparency: a Signed Tree Root + a user's Merkle inclusion proof.
export interface STR {
  epoch: number;
  root: string;
  prev_hash: string;
  server: string;
  timestamp: number;
  signature: string;
}

export interface TransparencyProof {
  str: STR;
  server_key: string;
  user_id: string;
  index: number;
  audit_path: string[];
  key_state_hash: string;
  included: boolean;
}

export interface TransparencyRoot {
  str: STR;
  server_key: string;
}
